<template>
  <TransitionRoot appear :show="showModal" as="template">
    <Dialog as="div" @close="closeModal">
      <div class="fixed inset-0 z-30 overflow-y-auto">
        <div class="min-h-screen text-center flex">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-10"
            leave="duration-200 ease-in"
            leave-from="opacity-10"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-black opacity-30" />
          </TransitionChild>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-50"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-50"
          >
            <div
              class="inline-block w-full my-5 mx-3 lg:w-2/3 md:mx-auto max-w-screen-md m-auto overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded"
            >
              <div class="flex flex-row">
                <div class="flex-1"></div>
                <div class="flex-4 pr-2 pt-2 cursor-pointer" @click="closeModal(false)">
                  <XIcon class="text-brand-dark-gray w-7 h-7" ref="close" />
                </div>
              </div>

              <template v-if="showModal">
                <div class="pb-5 px-5 md:px-8">
                  <h2 class="text-xl text-brand-black py-2">
                    {{ mode === "add" ? "Add User to" : "Update User in" }}
                    {{ company.name }}
                  </h2>

                  <p v-if="mode === 'add'" class="py-2">
                    Include the user's name and email address
                  </p>

                  <div class="flex flex-col py-1">
                    <label for="name" class="block">
                      Name
                      <span class="text-brand-red">*</span>
                    </label>
                    <SingleLineInput
                      id="name"
                      type="text"
                      :value="user.name"
                      @input="user.name = $event.target.value"
                      placeholder="Enter user's name"
                      :disabled="mode === 'update'"
                    ></SingleLineInput>
                  </div>

                  <div class="flex flex-col py-1">
                    <label for="name" class="block">
                      Email
                      <span class="text-brand-red">*</span>
                    </label>
                    <SingleLineInput
                      id="email"
                      type="email"
                      :value="user.email"
                      @input="user.email = $event.target.value"
                      placeholder="Enter user's email address"
                      :disabled="mode === 'update'"
                    ></SingleLineInput>
                  </div>

                  <div class="flex flex-col py-1" v-if="mode === 'add'">
                    <label for="name" class="block"> Phone </label>
                    <SingleLineInput
                      id="phone"
                      type="text"
                      :value="user.phone"
                      @input="user.phone = $event.target.value"
                      :required="true"
                      mask="phone"
                      placeholder="(123) 456-7890"
                    ></SingleLineInput>
                  </div>

                  <div class="py-1"></div>

                  <div class="flex flex-row py-1" v-if="mode === 'add'">
                    <input
                      id="copySettings"
                      type="checkbox"
                      class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                      v-model="user.copySettings"
                    />
                    <div class="flex flex-col ml-2">
                      <label for="copySettings" class="block text-sm">Copy Company Settings</label>
                      <p class="text-brand-mid-gray text-xs py-1">
                        Website, company name, logo, etc.
                      </p>
                    </div>
                  </div>

                  <div class="flex flex-row py-1 my-1" v-if="mode === 'add'">
                    <input
                      id="createDefaultApptemplate"
                      type="checkbox"
                      class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                      v-model="user.createDefaultApptemplate"
                    />
                    <div class="flex flex-col ml-2">
                      <label for="createDefaultApptemplate" class="block text-sm"
                        >Create starting application form</label
                      >
                    </div>
                  </div>

                  <div class="flex flex-col px-5 pb-2" v-if="user.createDefaultApptemplate">
                    <div class="flex flex-col w-1/2 py-1">
                      <label for="slug" class="block text-sm"> Slug </label>
                      <SingleLineInput
                        id="slug"
                        type="text"
                        :value="user.slug"
                        @input="user.slug = $event.target.value"
                      ></SingleLineInput>
                    </div>

                    <div class="flex flex-col">
                      <p class="text-brand-dark-gray text-sm py-1">
                        Select the reports for this application form
                      </p>
                      <div
                        class="flex items-center py-1"
                        v-for="rsp in getCurrentUser.report_source_profiles.filter((r) => r.active)"
                        :key="rsp.id"
                      >
                        <input
                          :id="`rsp-${rsp.id}`"
                          type="checkbox"
                          class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                          :value="rsp.source_identifier"
                          v-model="user.selectedRsp"
                        />
                        <div class="flex flex-col ml-2">
                          <label :for="`rsp-${rsp.id}`" class="block text-sm">{{
                            rsp.source_short_label
                          }}</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-row py-1">
                    <input
                      id="isAgent"
                      type="checkbox"
                      class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                      v-model="user.isAgent"
                    />
                    <div class="flex flex-col ml-2">
                      <label for="isAgent" class="block text-sm">Show in agents dropdown</label>
                      <p class="text-brand-mid-gray text-xs py-1">
                        Applicants can choose this user when filling out the application form
                      </p>
                    </div>
                  </div>

                  <div class="flex flex-row py-2">
                    <input
                      id="canCreate"
                      type="checkbox"
                      class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                      v-model="user.canCreate"
                    />
                    <label for="canCreate" class="ml-2 block text-sm">Can Create Forms</label>
                  </div>

                  <div class="flex flex-row py-2">
                    <input
                      id="showMyPayments"
                      type="checkbox"
                      class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                      v-model="user.showMyPayments"
                    />
                    <label for="showMyPayments" class="ml-2 block text-sm">Show My Payments</label>
                  </div>

                  <div class="flex flex-row py-2">
                    <input
                      id="showAcceptRejectButton"
                      type="checkbox"
                      class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                      v-model="user.showAcceptRejectButton"
                    />
                    <label for="showAcceptRejectButton" class="ml-2 block text-sm"
                      >Show Accept / Reject Button</label
                    >
                  </div>

                  <div class="flex items-center py-3" v-if="errorMessage">
                    <p class="text-brand-red">{{ errorMessage }}</p>
                  </div>

                  <div class="flex flex-row w-full py-3 justify-center">
                    <div class="mr-10">
                      <SecondaryButton
                        @click="closeModal(false)"
                        :classes="'border-brand-mid-gray'"
                      >
                        Cancel
                      </SecondaryButton>
                    </div>
                    <div class="mr-10" v-if="mode === 'update'">
                      <PrimaryButton
                        @click="removeUser"
                        :disabled="isCompanyLoading"
                        :is-loading="isCompanyLoading"
                        :classes="'bg-brand-red border-brand-red hover:bg-brand-red'"
                      >
                        Remove User
                      </PrimaryButton>
                    </div>
                    <div class="">
                      <PrimaryButton
                        @click="addUpdateUser"
                        :disabled="isCompanyLoading"
                        :is-loading="isCompanyLoading"
                      >
                        {{ mode === "add" ? "Add " : "Update " }} User
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script>
import { mapGetters } from "vuex"
import validator from "validator"

import { COMPANY_ADD_USER, COMPANY_UPDATE_USER, COMPANY_REMOVE_USER } from "@/store/types"

import { TransitionRoot, TransitionChild, Dialog, DialogOverlay } from "@headlessui/vue"

import { XIcon } from "@heroicons/vue/outline"

export default {
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    company: {
      type: Object
    },
    membership: {
      type: Object
    },
    mode: {
      type: String
    }
  },
  components: {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    XIcon
  },
  computed: {
    ...mapGetters(["isCompanyLoading", "getCurrentUser"])
  },
  data() {
    return {
      user: {
        selectedRsp: []
      },
      errorMessage: ""
    }
  },
  watch: {
    showModal: {
      handler(val) {
        if (val) {
          if (this.membership) {
            this.user = {
              name: this.membership.name,
              email: this.membership.user.username,
              canAdmin: this.membership.can_admin,
              canEdit: this.membership.can_edit,
              canCreate: this.membership.can_create,
              isAgent: this.membership.is_agent,
              showMyPayments: this.membership.landlord.show_my_payments,
              showAcceptRejectButton: this.membership.landlord.show_accept_reject_button
            }
          }
        } else {
          this.user = {
            selectedRsp: []
          }
        }
      }
    }
  },
  mounted() {},
  methods: {
    closeModal(reload = false) {
      this.$emit("close", reload)
    },
    async removeUser() {
      try {
        await this.$store.dispatch(COMPANY_REMOVE_USER, {
          id: this.company.id,
          userId: this.membership.user.id
        })
        this.closeModal()
      } catch (err) {
        console.log(err)
      }
    },
    async addUpdateUser() {
      if (!validator.isEmail(this.user.email)) {
        this.errorMessage = "Valid email is required."
        return
      }

      if (this.mode === "add") {
        try {
          await this.$store.dispatch(COMPANY_ADD_USER, {
            id: this.company.id,
            userData: this.user
          })
          this.closeModal()
        } catch (err) {
          console.log(err)
          this.errorMessage = err.response.data.error
        }
      } else {
        try {
          await this.$store.dispatch(COMPANY_UPDATE_USER, {
            id: this.company.id,
            userData: this.user
          })
          this.closeModal()
        } catch (err) {
          console.log(err)
          this.errorMessage = err.response.data.error
        }
      }
    }
  }
}
</script>
